import React, { useEffect, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';

import { UseOutletContextStatement } from './Statement';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { StatementDA } from '../../../../services/admin/statement.service';
import { FiscalYearDA } from '../../../../services/admin/fiscal.year.service';
import { AccountingAccountDA } from '../../../../services/admin/accounting.account.service';
import { CompanyPeriods } from '../../../../models/company.periods.model';
import { Error } from '../../../../models/error.model';
import { Period } from '../../../../models/period.model';
import { GlobalSwal } from '../../../../styles/swal.style';
import { getError } from '../../../../languages/translations/response';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';
import { formatMoney } from '../../../../scripts/format.value.script';
import { taxMethodCompanyValue } from '../../../../libraries/tax.method.company.library';
import { taxDeclarationCompanyValue } from '../../../../libraries/tax.declaration.company.library';
import { taxPeriodCompanyValue } from '../../../../libraries/tax.statement.company.library';
import { sortColumn } from '../../../../scripts/table.script';
import { dateFormat } from '../../../../scripts/date.time.format.script';
import { modalShow } from '../../../../utils/modal.util';
import { taxStatusPeriodValue } from '../../../../libraries/period.status.library';
import { taxRateCompany } from '../../../../scripts/list.selected.script';

import AppPreloader from '../../../../components/Preloader';
import AppSortTable from '../../../../components/element/SortTable';
import AppModalPeriodExport from '../../../../components/modal/ModalPeriodExport';
import EmptyState from '../../../../components/element/EmptyState';

import background from '../../../../assets/themes/images/question.svg';

export interface StatementListPageProps {};

let errorResponse: Error, periodsResponse: CompanyPeriods;

interface EligibilityInfo {
  eligible: boolean;
  reason?: string;
  orphan_count?: number;
  correction_period_id?: string;
}

const StatementListPage: React.FunctionComponent<StatementListPageProps> = props => {
  const {setRoute, companyForUser, settingsForCompany} = UseOutletContextStatement()
  const {lang} = StateLanguage()
  const navigate = useNavigate()

  const [mounted, setMounted] = useState(false)
  const [filter, setFilter] = useState({sort_field: '', sort_mode: ''})
  const [period, setPeriod] = useState('')
  const [periods, setPeriods] = useState<Period[] | null>(null)
  const [periodicity, setPeriodicity] = useState('')
  const [activePeriod, setActivePeriod] = useState<any>(null)
  const [fiscalYears, setFiscalYears] = useState<any[]>([])
  const [eligibilityMap, setEligibilityMap] = useState<Record<string, EligibilityInfo>>({})
  const [creatingCorrection, setCreatingCorrection] = useState(false)

  const deducePeriodicity = (periodName: string): string => {
    if (periodName.includes('-Q')) return 'quarterly';
    if (periodName.includes('-S')) return 'semiannual';
    if (periodName.includes('-M')) return 'monthly';
    return '';
  };

  const loadPeriods = async (id_company: string) => {
    await StatementDA.getPeriods(id_company, filter.sort_field, filter.sort_mode).then( (response) => {
      if (response.status === 200) {
        const raw = response.data

        if (Array.isArray(raw)) {
          const tempPeriods: Period[] = raw.map((item: any) => ({
            ...item,
            id: item.id ?? item._id ?? '',
            created_at: item.created_at ?? item.createdAt ?? '',
            closed_at: item.closed_at ?? item.closed_at ?? '',
            filed_at: item.filed_at ?? item.filed_at ?? '',
            vat_net: item.vat_net ?? 0,
            output: item.output ?? 0,
            input: item.input ?? 0
          }))
          setPeriods(tempPeriods)
        } else {
          periodsResponse = raw
          setPeriods(periodsResponse.periods ?? [])
          if (periodsResponse.filter) {
            setFilter({sort_field: periodsResponse.filter.sort_field, sort_mode: periodsResponse.filter.sort_mode})
          }
        }
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    })
  }

  const loadFiscalYears = async (id_company: string) => {
    try {
      const response = await FiscalYearDA.getFiscalYears(id_company)
      if (response.status === 200) {
        const data = response.data
        const items = Array.isArray(data) ? data : data?.items ?? data?.fiscal_years ?? []
        setFiscalYears(items)
        // Load eligibility for each fiscal year
        for (const fy of items) {
          const fyId = fy.id ?? fy._id
          if (fyId) loadEligibility(id_company, fyId)
        }
      }
    } catch {
      // silent
    }
  }

  const loadEligibility = async (id_company: string, id_fiscal_year: string) => {
    try {
      const response = await StatementDA.getAnnualCorrectionEligibility(id_company, id_fiscal_year)
      if (response.status === 200 && response.data) {
        setEligibilityMap(prev => ({ ...prev, [id_fiscal_year]: response.data }))
      }
    } catch {
      // silent
    }
  }

  const handleCreateCorrection = async (fyId: string, fyName: string) => {
    if (!companyForUser) return

    const result = await GlobalSwal.fire({
      title: `${lang.labels.createAnnualCorrection} — ${fyName}`,
      text: lang.labels.confirmCreateAnnualCorrection,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: lang.labels.create,
      cancelButtonText: lang.labels.cancel,
    })

    if (!result.isConfirmed) return

    setCreatingCorrection(true)
    try {
      const response = await StatementDA.createAnnualCorrection(companyForUser.id, fyId)
      if (response.status === 200 || response.status === 201) {
        const correctionPeriod = response.data
        const correctionId = correctionPeriod?.id ?? correctionPeriod?._id
        await loadPeriods(companyForUser.id)
        await loadFiscalYears(companyForUser.id)
        if (correctionId) {
          navigate(`/main/accounting/vat-return/view/${correctionId}`)
        }
      } else {
        const error = response.data as any
        GlobalSwal.fire({ title: error?.message ?? getError(error?.code, lang.code, error?.message), icon: 'error' })
      }
    } catch {
      GlobalSwal.fire({ title: lang.labels.error, icon: 'error' })
    } finally {
      setCreatingCorrection(false)
    }
  }

  const executeViewPeriodCalculation = (item: string) => {
    navigate('/main/accounting/statement/calculation')
  }

  const executeViewPeriodTickets = (item: string) => {
    navigate('/main/accounting/statement/ticket')
  }

  const executeShowModalPeriodExport = (item: string) => {
    setPeriod(item)
    modalShow('modal-period-export')
  }
  const executeSortList = (item: string) => {
    if (companyForUser) {
      sortColumn(item, filter)
      loadPeriods(companyForUser.id)
    }
  }

  const correctionButton = (fyId: string, fyName: string) => {
    const elig = eligibilityMap[fyId]
    if (!elig) return null

    if (elig.eligible) {
      return (
        <button
          className="btn btn-sm btn-light-success py-1 px-3"
          onClick={() => handleCreateCorrection(fyId, fyName)}
          disabled={creatingCorrection}
        >
          {creatingCorrection
            ? <span className="spinner-border spinner-border-sm align-middle"></span>
            : `${lang.labels.createAnnualCorrection} (${elig.orphan_count ?? 0})`
          }
        </button>
      )
    }

    if (elig.reason === 'already_exists' && elig.correction_period_id) {
      return (
        <Link to={`/main/accounting/vat-return/view/${elig.correction_period_id}`} className="btn btn-sm btn-light-primary py-1 px-3">
          {lang.labels.viewAnnualCorrection}
        </Link>
      )
    }

    const reasonText = elig.reason === 'periods_not_all_filed' ? lang.labels.periodsNotAllFiled
      : elig.reason === 'no_orphans' ? lang.labels.noOrphans
      : elig.reason === 'no_standard_period' ? lang.labels.noOrphans
      : ''

    if (reasonText) {
      return (
        <span className="text-muted fs-8" title={reasonText}>{reasonText}</span>
      )
    }

    return null
  }

  useEffect( () => {
    if (companyForUser) {
      loadPeriods(companyForUser.id)
      loadFiscalYears(companyForUser.id)

      AccountingAccountDA.getFiscalPeriods(companyForUser.id).then(response => {
        if (response.status === 200) {
          const fiscalPeriods = Array.isArray(response.data) ? response.data : response.data?.periods ?? []
          const openPeriod = fiscalPeriods.find((p: any) => p.status === 'open')
          setActivePeriod(openPeriod || null)
          if (fiscalPeriods.length > 0) {
            setPeriodicity(deducePeriodicity(fiscalPeriods[0].name ?? ''))
          }
        }
      })
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser])

  useEffect( () => {
    setMounted(true)
    setRoute({path: {root: lang.labels.accounting, branch: lang.labels.listVatStatements}, company: true})

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  // Group periods by fiscal year
  const periodsByFy = new Map<string, Period[]>()
  const ungrouped: Period[] = []
  if (periods) {
    for (const p of periods) {
      const fyId = (p as any).id_fiscal_year
      if (fyId) {
        const list = periodsByFy.get(fyId) || []
        list.push(p)
        periodsByFy.set(fyId, list)
      } else {
        ungrouped.push(p)
      }
    }
  }

  // Build ordered list of fiscal year groups
  const fyGroups: Array<{ fyId: string; fyName: string; periods: Period[] }> = []
  for (const fy of fiscalYears) {
    const fyId = fy.id ?? fy._id
    const fyPeriods = periodsByFy.get(fyId) || []
    if (fyPeriods.length > 0) {
      fyGroups.push({ fyId, fyName: fy.name ?? fyId, periods: fyPeriods })
    }
  }
  // Add any ungrouped periods
  if (ungrouped.length > 0) {
    fyGroups.push({ fyId: '', fyName: '', periods: ungrouped })
  }
  // If no grouping at all, show flat
  const hasFyGrouping = fiscalYears.length > 0 && periodsByFy.size > 0

  const renderPeriodRow = (item: Period, index: number) => (
    <tr key={index} className="row-dashed cursor-pointer" onClick={() => navigate(`/main/accounting/vat-return/view/${item.id}`)}>
      <td>
        <div className="d-flex flex-column">
          <div className="d-flex align-items-center">
            <span className="text-dark fw-bolder text-hover-primary mb-1">{item.name}</span>
            {(item as any).type === 'annual_correction' &&
              <span className="badge badge-light-warning ms-2 fs-9">{lang.labels.annualCorrection}</span>
            }
          </div>
          <small>{dateFormat(lang.code, item.start_date)} - {dateFormat(lang.code, item.end_date)}</small>
        </div>
      </td>
      <td>{taxStatusPeriodValue(lang, item.status)}</td>
      <td>{taxMethodCompanyValue(lang, item.regime)}</td>
      <td>{taxDeclarationCompanyValue(lang, item.method)}</td>
      <td className="fs-7">{dateFormat(lang.code, item.created_at)}</td>
      <td className="text-end">{companyForUser!.currency.code} {formatMoney(item.vat_net)}</td>
      <td className="text-end">
        <Dropdown>
          <Dropdown.Toggle variant="btn btn-sm btn-light btn-active-light-primary">
            <div className="d-flex align-items-center">
              {lang.labels.actions}
              <span className="svg-icon svg-icon-5 me-0">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M11.4343 12.7344L7.25 8.55005C6.83579 8.13583 6.16421 8.13584 5.75 8.55005C5.33579 8.96426 5.33579 9.63583 5.75 10.05L11.2929 15.5929C11.6834 15.9835 12.3166 15.9835 12.7071 15.5929L18.25 10.05C18.6642 9.63584 18.6642 8.96426 18.25 8.55005C17.8358 8.13584 17.1642 8.13584 16.75 8.55005L12.5657 12.7344C12.2533 13.0468 11.7467 13.0468 11.4343 12.7344Z" fill="black"></path>
                </svg>
              </span>
            </div>
          </Dropdown.Toggle>
          <Dropdown.Menu renderOnMount popperConfig={{ strategy: "fixed" }} variant="menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-600 menu-state-bg-light-primary w-auto p-3 fs-7" align="end">
            <Dropdown.Item bsPrefix="menu-item" onClick={() => executeViewPeriodTickets(item.id)}>
              <p className="menu-link fw-bold px-3 py-1 m-0">{lang.labels.tickets}</p>
            </Dropdown.Item>
            <Dropdown.Item bsPrefix="menu-item" onClick={() => executeShowModalPeriodExport(item.id)}>
              <p className="menu-link fw-bold px-3 py-1 m-0">{lang.labels.export}</p>
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </td>
    </tr>
  )

  return (
    <div className="w-100 h-100">
      { companyForUser && settingsForCompany && periods
        ?
        <>
          <div className="card card-flush mb-5">
            <div className="card-header">
              <div className="card-title">
                <h3>{lang.labels.vatSettingsMap}</h3>
              </div>
              <div className="card-toolbar">
                <div className="d-flex justify-content-end align-items-center">
                  <Link to="/main/company/update?tab=accounting" className="btn btn-primary">
                    <div className="d-flex align-items-center">
                      <span className="svg-icon svg-icon-2 ms-0">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path opacity="0.3" fillRule="evenodd" clipRule="evenodd" d="M2 4.63158C2 3.1782 3.1782 2 4.63158 2H13.47C14.0155 2 14.278 2.66919 13.8778 3.04006L12.4556 4.35821C11.9009 4.87228 11.1726 5.15789 10.4163 5.15789H7.1579C6.05333 5.15789 5.15789 6.05333 5.15789 7.1579V16.8421C5.15789 17.9467 6.05333 18.8421 7.1579 18.8421H16.8421C17.9467 18.8421 18.8421 17.9467 18.8421 16.8421V13.7518C18.8421 12.927 19.1817 12.1387 19.7809 11.572L20.9878 10.4308C21.3703 10.0691 22 10.3403 22 10.8668V19.3684C22 20.8218 20.8218 22 19.3684 22H4.63158C3.1782 22 2 20.8218 2 19.3684V4.63158Z" fill="black"/>
                          <path d="M10.9256 11.1882C10.5351 10.7977 10.5351 10.1645 10.9256 9.77397L18.0669 2.6327C18.8479 1.85165 20.1143 1.85165 20.8953 2.6327L21.3665 3.10391C22.1476 3.88496 22.1476 5.15129 21.3665 5.93234L14.2252 13.0736C13.8347 13.4641 13.2016 13.4641 12.811 13.0736L10.9256 11.1882Z" fill="black"/>
                          <path d="M8.82343 12.0064L8.08852 14.3348C7.8655 15.0414 8.46151 15.7366 9.19388 15.6242L11.8974 15.2092C12.4642 15.1222 12.6916 14.4278 12.2861 14.0223L9.98595 11.7221C9.61452 11.3507 8.98154 11.5055 8.82343 12.0064Z" fill="black"/>
                        </svg>
                      </span>
                      {lang.labels.editAccountingSettings}
                    </div>
                  </Link>
                </div>
              </div>
            </div>
            <div className="card-body pt-0">
              <div className="row">
                <div className="col-12 col-xl-8 col-xxl-7">
                  <table className="table gs-0 gy-1 m-0 mb-5">
                    <tbody>
                      <tr className="d-none">
                        <td className="w-200px fw-bold text-muted">{lang.labels.registerNumber}:</td>
                        <td className="min-w-200px fw-bold fs-6">{(companyForUser.register_number ?? '').length > 0 ? companyForUser.register_number : lang.labels.none}</td>
                      </tr>
                      <tr>
                        <td className="w-200px fw-bold text-muted">{lang.labels.vatNumber}:</td>
                        <td className="min-w-200px fw-bold fs-6">{companyForUser.vat_number?.length > 0 ? companyForUser.vat_number : lang.labels.none}</td>
                      </tr>
                      <tr>
                        <td className="w-200px fw-bold text-muted">{lang.labels.vatAccountingType}:</td>
                        <td className="min-w-200px fw-bold fs-6">{settingsForCompany?.subject_tax ? `${taxMethodCompanyValue(lang, settingsForCompany.tax_method)}${taxRateCompany(settingsForCompany?.tax_fees ?? [], settingsForCompany.tax_method)}` : lang.labels.none}</td>
                      </tr>
                      <tr>
                        <td className="w-200px fw-bold text-muted">{lang.labels.declarationMethod}:</td>
                        <td className="min-w-200px fw-bold fs-6">{settingsForCompany?.subject_tax && settingsForCompany.tax_declaration?.length > 0 ? taxDeclarationCompanyValue(lang, settingsForCompany.tax_declaration) : lang.labels.none}</td>
                      </tr>
                      <tr>
                        <td className="w-200px fw-bold text-muted">{lang.labels.vatCalculationPeriod}:</td>
                        <td className="min-w-200px fw-bold fs-6">{periodicity ? taxPeriodCompanyValue(lang, periodicity) : lang.labels.none}</td>
                      </tr>
                      <tr>
                        <td className="w-200px fw-bold text-muted">{lang.labels.upcomingVatStatement}:</td>
                        <td className="min-w-200px fw-bold fs-6">{activePeriod ? `${activePeriod.name} (${dateFormat(lang.code, activePeriod.start_date)} - ${dateFormat(lang.code, activePeriod.end_date)})` : lang.labels.none}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="col-12 col-xl-4  col-xxl-5">
                  <div className="notice d-flex align-items-center bg-light-warning border border-warning border-dashed rounded p-5 h-100 overflow-hidden">
                    <span className="svg-icon svg-icon-3x svg-icon-warning me-5">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <rect opacity="0.3" x="2" y="2" width="20" height="20" rx="10" fill="black" />
                        <rect x="11" y="14" width="7" height="2" rx="1" transform="rotate(-90 11 14)" fill="black" />
                        <rect x="11" y="17" width="2" height="2" rx="1" transform="rotate(-90 11 17)" fill="black" />
                      </svg>
                    </span>
                    <div className="d-grid">
                      <span className="text-start text-dark fw-bolder mb-2">{lang.labels.important}</span>
                      <span className="text-justify text-secondary fw-bold text-break">
                        {lang.labels.ifYouWishChangeVatRegimePleaseContact}
                        <a href="mailto:support@digitaladmin.ch" className="text-primary text-lowercase fw-bolder mx-1">support@digitaladmin.ch</a>
                        {lang.labels.orCall}
                        <a href="tel:+41792830646" className="text-primary text-lowercase fw-bolder mx-1">+41 79 283 06 46</a>
                        .
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="card card-flush">
            <div className="card-header">
              <div className="card-title">
                <div className="d-flex align-items-center">
                  <div className="symbol symbol-circle me-3">
                    <div className="symbol-label bg-transparent border border-secondary border-dashed h-25px w-25px pb-1">
                      <span className="svg-icon svg-icon-8 svg-icon-primary">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M21 7H3C2.4 7 2 6.6 2 6V4C2 3.4 2.4 3 3 3H21C21.6 3 22 3.4 22 4V6C22 6.6 21.6 7 21 7Z" fill="black"/>
                          <path opacity="0.3" d="M21 14H3C2.4 14 2 13.6 2 13V11C2 10.4 2.4 10 3 10H21C21.6 10 22 10.4 22 11V13C22 13.6 21.6 14 21 14ZM22 20V18C22 17.4 21.6 17 21 17H3C2.4 17 2 17.4 2 18V20C2 20.6 2.4 21 3 21H21C21.6 21 22 20.6 22 20Z" fill="black"/>
                        </svg>
                      </span>
                    </div>
                  </div>
                  <h6>{lang.labels.listPeriodsStatement}</h6>
                </div>
              </div>
              <div className="card-toolbar">
                <Link to="/main/accounting/statement/ticket" className="btn btn-sm btn-light-warning">
                  {lang.labels.tickets}
                </Link>
              </div>
            </div>
            <div className="card-body pt-0">
              <div className="dataTables_wrapper">
                <div className="table-responsive">
                  <table className="table align-middle table-row-dashed mb-2">
                    <thead className="border-bottom">
                      <tr className="text-start text-muted text-uppercase fw-bolder fs-7">
                        <th className="min-w-200px">
                          <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("name")}>
                            <span className="me-1">{lang.labels.name}</span>
                            <AppSortTable attribute={"name"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                          </div>
                        </th>
                        <th className="min-w-100px">
                          <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("status")}>
                            <span className="me-1">{lang.labels.status}</span>
                            <AppSortTable attribute={"status"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                          </div>
                        </th>
                        <th className="min-w-100px">
                          <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("regime")}>
                            <span className="me-1">{lang.labels.regime}</span>
                            <AppSortTable attribute={"regime"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                          </div>
                        </th>
                        <th className="min-w-100px">
                          <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("method")}>
                            <span className="me-1">{lang.labels.method}</span>
                            <AppSortTable attribute={"method"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                          </div>
                        </th>
                        <th className="min-w-100px">
                          <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("date")}>
                            <span className="me-1">{lang.labels.createdDate}</span>
                            <AppSortTable attribute={"date"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                          </div>
                        </th>
                        <th className="min-w-100px">
                          <div className="d-flex align-items-center justify-content-end text-nowrap cursor-pointer" onClick={() => executeSortList("amount")}>
                            <span className="me-1">{lang.labels.vatAmountDue}</span>
                            <AppSortTable attribute={"amount"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                          </div>
                        </th>
                        <th className="w-125px text-end">{lang.labels.actions}</th>
                      </tr>
                    </thead>
                    { periods.length > 0
                      ?
                      <tbody className="text-secondary fw-bold">
                        { hasFyGrouping
                          ? fyGroups.map(group => (
                            <React.Fragment key={group.fyId || '_ungrouped'}>
                              { group.fyId &&
                                <tr className="bg-light">
                                  <td colSpan={6} className="fw-bolder fs-7 py-2">
                                    <span className="me-2">{lang.labels.fiscalYear}: {group.fyName}</span>
                                  </td>
                                  <td className="text-end py-2">
                                    {correctionButton(group.fyId, group.fyName)}
                                  </td>
                                </tr>
                              }
                              {group.periods.map((item, index) => renderPeriodRow(item, index))}
                            </React.Fragment>
                          ))
                          : periods.map((item, index) => renderPeriodRow(item, index))
                        }
                      </tbody>
                      :
                      <tbody>
                        <tr>
                          <td colSpan={7} className="p-0 border-0">
                            { !settingsForCompany?.subject_tax
                              ? <EmptyState illustration={background} illustrationAlt="" title={lang.labels.companyNotSubjectToVat} description={lang.labels.companyNotSubjectToVatDescription} />
                              : <EmptyState illustration={background} illustrationAlt="" title={lang.labels.noRecordsFound} />
                            }
                          </td>
                        </tr>
                      </tbody>
                    }
                  </table>
                </div>
              </div>
            </div>
          </div>
        </>
        :
        <AppPreloader></AppPreloader>
      }
      <AppModalPeriodExport companyForUser={companyForUser} period={period}></AppModalPeriodExport>
    </div>
  )
};

export default StatementListPage;
